import { ChevronUpIcon, CopyIcon, MixerHorizontalIcon, Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import { Badge, Button, Card, ChevronDownIcon, Flex, Text } from '@radix-ui/themes';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { ConfirmAlertDialog } from '../../components/common/ConfirmAlertDialog';
import { Lists } from '../../components/common/Lists';
import TableComponent from '../../components/common/Tables';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import { useAppDispatch } from '../../hooks/hooks';
import { useGetAcademicYearQuery } from '../../hooks/useGetAcademicYearQuery';
import { useGetCoursesQuery } from '../../hooks/useGetCoursesQuery';
import { useGetEnrollmentsQuery } from '../../hooks/useGetEnrollmentsQuery';
import { setLoaderAvailable } from '../../redux/slices/loaderController';
import { setUpdateEnrollmentData } from '../../redux/slices/school/student';
import { setUserData } from '../../redux/slices/users/user';
import { routes } from '../../routes';
import { getStudentDebtsByEnrollment } from '../../services/finance/studentDebt';
import { deleteEnrollment } from '../../services/school/student/deleteEnrollments';
import { getEnrollmentsDocuments } from '../../services/school/student/getEnrollmentsDocuments';
import { getStudentById } from '../../services/school/student/getStudentByCode';
import { updateEnrollment } from '../../services/school/student/updateEnrollment';
import { getUserById } from '../../services/user/getUsers';
import { CreateStudentType, EnrollmentType } from '../../types/Student';
import { CancelEnrollmentReasons, Regimes } from '../../utils/codes';
import { Key_enrollments, PAGE_NUMBER, PAGE_SIZE } from '../../utils/constants';
import { stringToBoolean } from '../../utils/utilities';
import RadioGroupComponent from '../../components/inputs/RadioGroup/RadioGroupComponent';
import { useGetCurrentAcademicYearQuery } from '../../hooks/useGetCurrentAcademicYearQuery';

const columnNamesMap: { [key: string]: string } = {
  fullname: 'Nome',
  student: 'Estudante',
  course: 'Curso',
  academicYear: 'Ano academico',
  isValidatedIcon: 'Válida',
};

export const Enrollments = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [deleteItemEnrollment, setDeleteItemEnrollment] = useState<EnrollmentType | null>(null);
  const [cancelEnrollment, setCancelEnrollment] = useState<EnrollmentType | null>(null);
  const mutationUpdateEnrollment = useMutation({ mutationFn: updateEnrollment, mutationKey: "updateEnrollment" })

  const openDeleteItemEnrollmentDialog = (s: EnrollmentType) => {
    s.isValidatedIcon = '';
    setDeleteItemEnrollment(s);
  };
  const openCancelItemEnrollmentDialog = (s: EnrollmentType) => {
    s.isValidatedIcon = '';
    setCancelEnrollment(s);
  };
  const confirmDeleteItemEnrollment = async () => {
    if (deleteItemEnrollment) {
      await handleDelete(deleteItemEnrollment!)
    }
  };
  const confirmCancelItemEnrollment = async () => {
    if (cancelEnrollment) {
      await handleCancelEnrollment(cancelEnrollment!)
    }
  };

  type Filters = {
    IdCourse?: string;
    IdAcademicYear?: string;
    isCanceledReason?: string;
    Regime?: number;
  }

  const { control, formState: { errors }, getValues, setValue, watch, reset } = useForm<Filters>({
    defaultValues: { 'isCanceledReason': '', IdCourse: '', IdAcademicYear: '' },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });

  const { data: academicYears, isLoading: isLoadingAcademicYears, isError: errorAcademicYears } = useGetAcademicYearQuery({ PageNumber: 1, PageSize: 400 });
  const { data: currentYear } = useGetCurrentAcademicYearQuery();
  const { data: courses, isLoading: isLoadingCourses, isError: errorCourses } = useGetCoursesQuery({ PageNumber: 1, PageSize: 400 },
  );

  const filters = watch();

  const { data, status } = useGetEnrollmentsQuery({ PageNumber: currentPage, PageSize: pageSize, SearchTerm: searchTerm, ...filters });
  const [showFilters, setShowFilters] = useState(false);

  const mutationDelete = useMutation(deleteEnrollment, {
    onSuccess: () => {
      queryClient.invalidateQueries(Key_enrollments);
      toast.success('Apagado');
    },
    onError: () => {
      toast.error("Erro ao apagar.")
    }
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(PAGE_NUMBER);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };


  const handleClickCreate = (event: any): void => {
    navigate(routes.createEnrollment)
  }
  const handleEdit = async (enrollment: EnrollmentType) => {
    enrollment.isValidatedIcon = '';
    if (enrollment?.isCanceled) {
      toast.error(`Não pode editar, estudante ${CancelEnrollmentReasons.find(cer => cer.id === enrollment.isCanceledReason)}`);
      return;
    }
    try {
      const userData = await getUserById(enrollment.idUser)
      if (!userData) {
        toast.error("utilizador não encontrado.");
        return;
      }
      const studentData = await getStudentById(enrollment.idStudent);
      if (!studentData) {
        return;
      }
      if (!studentData) {
        toast.error("Estudante não encontrado.");
        return;
      }
      const { items } = await getEnrollmentsDocuments({ PageNumber: 1, PageSize: 100, IdEnrollment: enrollment.id })

      const enrollmentForm: CreateStudentType = {
        user: { ...userData, isForeign: stringToBoolean(studentData.isForeign), },
        documents: [],
        enrollment: {
          id: enrollment?.id,
          idStudent: enrollment?.idStudent,
          idCourse: enrollment?.idCourse,
          date: enrollment?.date,
          idAcademicYear: enrollment?.idAcademicYear,
          status: enrollment?.status,
          course: enrollment?.course,
          departament: enrollment?.departament,
          faculty: enrollment?.faculty,
          regime: enrollment.regime,
          subscribeStudent: enrollment.subscribeStudent ?? false,
          idPlan: enrollment?.idPlan ?? studentData?.idPlan ?? '',
        },
        idFaculty: enrollment.idFaculty ?? "",
        idDepartament: enrollment.idDepartament ?? "",
        idPlan: enrollment?.idPlan ?? studentData?.idPlan ?? '',
        storeDocuments: items,
        subscribeStudent: enrollment?.subscribeStudent ?? false,
        isInternStudent: true,
        student: { ...studentData, countryDescription: studentData.countryDescription ?? "", schoolCode: studentData.schoolCode ?? "", enrollmentYear: studentData.enrollmentYear },
        countryDescription: studentData.countryDescription ?? "",
        idUniversity: studentData.idUniversity ?? "",
      };
      try {
        const studentDebtsResponse = await getStudentDebtsByEnrollment(enrollment.id!);
        const studentDebtData = studentDebtsResponse?.[0];
        const paymentReferences = studentDebtData?.paymentReferences;
        const paymentTransactions = studentDebtData?.paymentTransactions;
        const paymentTransaction = paymentTransactions?.find(p => p?.status);
        const paymentReference = paymentReferences?.find(p => p?.status);
        enrollmentForm.payment = {
          entity: paymentReference?.entity,
          reference: paymentReference?.referenceCode,
          totalPrice: paymentReference?.amount,
          paymentProof: paymentTransaction?.paymentProof,
          idStudentDebt: studentDebtData?.id,
          isPaymentByEntityReference: isEmpty(paymentReference?.referenceCode) ? false : true
        }
      } catch (error) {
        console.log({ error })
      }
      dispatch(setUpdateEnrollmentData(enrollmentForm));
      navigate(routes.updateEnrollment);
    } catch (err: any) {
      toast.error(err?.message);
    }
  };
  const handleCancelEnrollment = async (enrollment: EnrollmentType) => {
    if (enrollment?.isCanceled) {
      toast.error(`Não pode cancelar, estudante ${CancelEnrollmentReasons.find(cer => cer.id === enrollment.isCanceledReason)}`);
      return;
    }
    try {
      const userData = await getUserById(enrollment.idUser)
      if (!userData) {
        toast.error("utilizador não encontrado.");
        return;
      }
      const studentData = await getStudentById(enrollment.idStudent);
      if (!studentData) {
        return;
      }
      if (!studentData) {
        toast.error("Estudante não encontrado.");
        return;
      }
      enrollment.isCanceled = true;
      enrollment.isCanceledReason = getValues('isCanceledReason')
      await mutationUpdateEnrollment.mutateAsync({ ...enrollment })
    } catch (err: any) {
      toast.error(err?.message);
    }
  };
  const isEmptyWatchCancelReasons = isEmpty(watch('isCanceledReason'));
  const handleDelete = (enrollment: EnrollmentType) => {
    if (enrollment?.isCanceled) {
      toast.error(`Não pode apagar, estudante ${CancelEnrollmentReasons.find(cer => cer.id === enrollment.isCanceledReason)}`);
      return;
    }
    try {
      mutationDelete.mutateAsync(enrollment.id)

    } catch (error: any) {
      toast.error(`${mutationDelete.error ?? error?.message}`);

    }
  };
  const handlePrintEnrollmentDocuments = async (enrollment: EnrollmentType) => {
    enrollment.isValidatedIcon = '';
    if (enrollment?.isCanceled) {
      toast.error(`Não pode imprimir, estudante ${CancelEnrollmentReasons.find(cer => cer.id === enrollment.isCanceledReason)}`);
      return;
    }
    try {
      const userData = await getUserById(enrollment.idUser)
      if (!userData) {
        toast.error("utilizador não encontrado.");
        return;
      }
      const studentData = await getStudentById(enrollment.idStudent);
      if (!studentData) {
        return;
      }
      if (!studentData) {
        toast.error("Estudante não encontrado.");
        return;
      }
      const { items } = await getEnrollmentsDocuments({ PageNumber: 1, PageSize: 100, IdEnrollment: enrollment.id })
      const enrollmentForm: CreateStudentType = {
        user: { ...userData, isForeign: stringToBoolean(studentData.isForeign) },
        documents: [],
        enrollment: {
          id: enrollment?.id,
          idStudent: enrollment?.idStudent,
          idCourse: enrollment?.idCourse,
          date: enrollment?.date,
          idAcademicYear: enrollment?.idAcademicYear,
          academicYear: enrollment?.academicYear,
          status: enrollment?.status,
          course: enrollment?.course,
          departament: enrollment?.departament,
          faculty: enrollment?.faculty,
          regime: enrollment.regime,
          subscribeStudent: enrollment.subscribeStudent ?? false
        },
        idUniversity: enrollment.idUniversity ?? "",
        idFaculty: enrollment.idFaculty ?? "",
        idDepartament: enrollment.idDepartament ?? "",
        idPlan: enrollment?.idPlan ?? '',
        storeDocuments: items,
        subscribeStudent: enrollment?.subscribeStudent ?? false,
        isInternStudent: true,
        student: { ...studentData, countryDescription: studentData.countryDescription ?? "", schoolCode: studentData.schoolCode ?? "" },
        countryDescription: studentData.countryDescription ?? "",
      };
      try {
        const studentDebtsResponse = await getStudentDebtsByEnrollment(enrollment.id!);
        const studentDebtData = studentDebtsResponse?.[0];
        const paymentReferences = studentDebtData?.paymentReferences;
        const paymentTransactions = studentDebtData?.paymentTransactions;
        const paymentTransaction = paymentTransactions?.find(p => p?.status);
        const paymentReference = paymentReferences?.find(p => p?.status);
        enrollmentForm.payment = {
          entity: paymentReference?.entity,
          reference: paymentReference?.referenceCode,
          totalPrice: paymentReference?.amount,
          paymentProof: paymentTransaction?.paymentProof,
          idStudentDebt: studentDebtData?.id,
          isPaymentByEntityReference: isEmpty(paymentReference?.referenceCode) ? false : true
        }
      } catch (error) {
        console.log({ error })
      }
      dispatch(setUpdateEnrollmentData(enrollmentForm));
      dispatch(setUserData(userData));
      navigate(routes.printEnrollmentDocs);
    } catch (err: any) {
      toast.error(err?.message);
    }
  };

  const handleValidate = async (enrollment: EnrollmentType) => {
    enrollment.isValidatedIcon = '';
    if (enrollment?.isCanceled) {
      toast.error(`Não pode validar, estudante ${CancelEnrollmentReasons.find(cer => cer.id === enrollment.isCanceledReason)}`);
      return;
    }
    if (enrollment.isValidated) {
      toast.error("Matrícula já validada.");
      return;
    }
    enrollment.isValidated = true;
    try {
      dispatch(setLoaderAvailable(true));
      await mutationUpdateEnrollment.mutateAsync(enrollment);
      toast.success("Matrícula validada");
      queryClient.invalidateQueries(Key_enrollments);
    } catch (error) {
      toast.error("Erro ao validar matrícula.");
      console.log({ error });
    } finally {
      dispatch(setLoaderAvailable(false));
    }
  }
  const handleInValidate = async (enrollment: EnrollmentType) => {
    enrollment.isValidatedIcon = '';
    if (enrollment?.isCanceled) {
      toast.error(`Não pode validar, estudante ${CancelEnrollmentReasons.find(cer => cer.id === enrollment.isCanceledReason)}`);
      return;
    }
    // if (enrollment.isValidated) {
    //   toast.error("Matrícula já validada.");
    //   return;
    // }
    try {
      enrollment.isValidated = false;
      dispatch(setLoaderAvailable(true));
      await mutationUpdateEnrollment.mutateAsync(enrollment);
      toast.success("Matrícula invalidada");
      queryClient.invalidateQueries(Key_enrollments);
    } catch (error) {
      toast.error("Erro ao invalidar matrícula.");
      console.log({ error });
    } finally {
      dispatch(setLoaderAvailable(false));
    }
  }

  const options = data?.items.map((enrollment) => {
    const opts = [];

    opts.push({
      label: 'Editar',
      onClick: () => handleEdit(enrollment),
      icon: <Pencil1Icon />
    });


    if (!enrollment.isValidated) {
      opts.push({
        label: 'Validar',
        onClick: () => handleValidate(enrollment),
        icon: <Pencil1Icon />
      });
    }

    opts.push({
      label: 'Imprimir documentos da matricula',
      onClick: () => handlePrintEnrollmentDocuments(enrollment),
      icon: <CopyIcon />
    });
    opts.push({
      label: 'Cancelar matricula',
      onClick: () => openCancelItemEnrollmentDialog(enrollment),
      icon: <TrashIcon />
    });
    opts.push({
      label: 'Excluir',
      onClick: () => openDeleteItemEnrollmentDialog(enrollment),
      icon: <TrashIcon />
    });

    return opts;
  }) || [];

  // const options = data?.items.map(() => [
  //   {
  //     label: 'Editar',
  //     onClick: (enrollment: any) => handleEdit(enrollment),
  //     icon: <Pencil1Icon />
  //   },
  //   {
  //     label: 'Validar',
  //     onClick: (enrollment: any) => handleValidate(enrollment),
  //     icon: <Pencil1Icon />
  //   },
  //   // {
  //   //   label: 'Invalidar',
  //   //   onClick: (enrollment: any) => handleInValidate(enrollment),
  //   //   icon: <Pencil1Icon />
  //   // },
  //   {
  //     label: 'Imprimir documentos da matricula',
  //     onClick: (enrollment: any) => handlePrintEnrollmentDocuments(enrollment),
  //     icon: <CopyIcon />
  //   },
  //   {
  //     label: 'Cancelar matricula',
  //     onClick: (enrollment: any) => openCancelItemEnrollmentDialog(enrollment),
  //     icon: <TrashIcon />
  //   },
  //   {
  //     label: 'Excluir',
  //     onClick: (enrollment: any) => openDeleteItemEnrollmentDialog(enrollment),
  //     icon: <TrashIcon />
  //   }
  // ]) || [];

  const handleCourseChange = async (idCourse: string) => {
    setValue('IdCourse', idCourse);
  }

  useEffect(() => {
    if (academicYears?.items && academicYears.items.length > 0) {
      if (!getValues('IdAcademicYear')) {

        const mostRecentYear = academicYears.items[academicYears.items.length - 1];
        setValue('IdAcademicYear', currentYear?.id ?? mostRecentYear.id);
      }
    }
  }, [academicYears, setValue, getValues]);
  return (
    <>
      <div style={{ marginBottom: '1rem' }}>

        <Flex gap="2" align="center" style={{ marginBottom: '1rem' }}>
          <Button
            variant="soft"
            onClick={() => setShowFilters(!showFilters)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <MixerHorizontalIcon />
            Filtros
            {showFilters ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </Button>

          <Button
            variant="ghost"
            color="gray"
            onClick={() => { reset(); }}
          >
            Limpar filtros
          </Button>
        </Flex>

        {showFilters && (
          <Card style={{ marginBottom: '1rem', padding: '1rem' }}>
            <Flex direction="column" gap="3">
              <Text size="2" weight="bold">Filtrar inscrições</Text>
              <Flex gap="4">
                <SelectComponent name="IdAcademicYear" label='Ano Acadêmico' control={control} errors={errors} style={{ width: '100%' }}
                  options={academicYears?.items.map(ac => ({
                    value: ac.id ?? "",
                    label: ac.year ?? ""
                  }))}
                  itemsError={errorAcademicYears}
                  itemsLoading={isLoadingAcademicYears}
                />
                <SelectComponent name="IdCourse" label='Curso' control={control} errors={errors} style={{ width: '100%' }}
                  options={courses?.items.map(course => ({
                    value: course.id ?? "",
                    label: course.name ?? ""
                  }))}
                  onSelect={
                    handleCourseChange
                  }
                  itemsError={errorCourses}
                  itemsLoading={isLoadingCourses}

                />

                <RadioGroupComponent
                  name="Regime"
                  label="Regime"
                  control={control}
                  errors={errors}
                  style={{ width: "100%" }}
                  options={Regimes.map((r) => ({
                    value: r.id ?? "",
                    label: r.description ?? "",
                  }))}
                  fieldProps={{
                    style: {
                      display: "flex",
                      flexDirection: "row",
                      gap: "16px",
                    },
                  }}
                />

              </Flex>
            </Flex>
          </Card>
        )}
      </div>

      <Lists
        status={status}
        TableComponent={() =>
          <TableComponent
            columns={Object.keys(columnNamesMap)}
            items={
              data?.items.map((e => {
                return {
                  ...e,
                  isValidatedIcon: e.isValidated ? (
                    <Badge color="green">Válido</Badge>
                  ) : (
                    <Badge color="gray">Pendente de validação</Badge>
                  ),
                }
              }))}
            options={options}
            columnNamesMap={columnNamesMap}
            totalPages={data?.totalPages || 1}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            onSearch={handleSearch}
            search={searchTerm}
            totalItems={data?.totalItems || 0}
          />
        }
        handleClickCreate={handleClickCreate}
      />
      <ConfirmAlertDialog
        open={!!deleteItemEnrollment}
        onOpenChange={(open) => !open && setDeleteItemEnrollment(null)}
        title="Esta operação é irreversível"
        item={deleteItemEnrollment}
        onConfirm={confirmDeleteItemEnrollment}
        onCancel={() => setDeleteItemEnrollment(null)}
      />
      <ConfirmAlertDialog
        open={!!cancelEnrollment}
        descriptionComponent={
          <>
            <SelectComponent
              name="isCanceledReason"
              label="Motivo"
              control={control}
              errors={errors}
              composedName={{ parent: "student", prop: 'province' }}
              options={CancelEnrollmentReasons?.map((r: { id: any, description: any }) => ({
                value: r.id,
                label: r.description ?? "",
              }))}
              style={{ width: '100%' }}
            // fieldProps={{ disabled: stringToBoolean(watchIsForeign) }}
            />
          </>
        }
        onOpenChange={(open) => !open && setCancelEnrollment(null)}
        title="Esta operação é irreversível"
        item={cancelEnrollment}
        disabledConfirm={isEmptyWatchCancelReasons}
        onConfirm={confirmCancelItemEnrollment}
        onCancel={() => setCancelEnrollment(null)}
      />

    </>
  );
}
